/**
 * TourRunner: a lightweight guided tour (no tour library).
 *
 * Dims the page with a cut-out around the step's `[data-help-id]` target and shows a card next to it
 * (top, bottom, left or right, flipping when there is no room; a bottom sheet at phone width; a
 * centred card when a step has no target or the target never appears). Next, Back, Skip and Done,
 * a step counter, Esc to skip and arrow keys to move. Scrolls the target into view, waits for it,
 * applies `step.route` first (e.g. "?tab=plan" merges into the URL), follows resizes and scrolling,
 * keeps focus in the card and gives it back afterwards, and stays still with reduced motion.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery, useReducedMotion } from '@mantine/hooks';
import { IconX } from '@tabler/icons-react';
import { Badge, Button, Group, Text, Title } from '@/components/ui';
import ActionIcon from '@/components/ui/ActionIcon';
import { usePreferencesStore } from '@/stores/preferencesStore';
import type { HelpTourStepView } from '@actschurch/ccms-sdk';
import type { ActiveTourRun, FinishTourOptions } from './HelpContext';
import { HelpHtml } from './HelpHtml';
import classes from './help.module.css';
import { findAnchor, pageHasAnchors, resolveStepRoute, sleep, sortedSteps, waitFor } from './helpUtils';

const LATER_STEP_WAIT_MS = 3000;
const GAP = 12;
const PAD = 6;
const EDGE = 16;

type Placement = 'top' | 'bottom' | 'left' | 'right' | 'center';

interface TourRunnerProps {
  run: ActiveTourRun;
  /** Done gives 'completed'; Skip, the X and Esc give 'dismissed'; following a link in a step adds navigated. */
  onFinish: (outcome: 'completed' | 'dismissed', options?: FinishTourOptions) => void;
  /** An auto-started tour whose page was left before it showed: end quietly, record nothing. */
  onAbandon: () => void;
}

function normalisePlacement(value: string | null | undefined): Placement {
  const v = (value ?? '').toLowerCase();
  return v === 'top' || v === 'left' || v === 'right' || v === 'center' ? v : 'bottom';
}

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

function toBox(r: DOMRect): Box {
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

/** Card position for a placement, flipping to the opposite side (then below) when it does not fit. */
function placeCard(target: Box, wanted: Placement, cardW: number, cardH: number, vw: number, vh: number): { top: number; left: number } {
  const fits = {
    bottom: target.top + target.height + PAD + GAP + cardH <= vh - EDGE,
    top: target.top - PAD - GAP - cardH >= EDGE,
    right: target.left + target.width + PAD + GAP + cardW <= vw - EDGE,
    left: target.left - PAD - GAP - cardW >= EDGE,
  };
  const order: Record<Exclude<Placement, 'center'>, Exclude<Placement, 'center'>[]> = {
    bottom: ['bottom', 'top', 'right', 'left'],
    top: ['top', 'bottom', 'right', 'left'],
    right: ['right', 'left', 'bottom', 'top'],
    left: ['left', 'right', 'bottom', 'top'],
  };
  const start = wanted === 'center' ? 'bottom' : wanted;
  const side = order[start].find((s) => fits[s]) ?? 'bottom';
  const centreX = clamp(target.left + target.width / 2 - cardW / 2, EDGE, Math.max(EDGE, vw - cardW - EDGE));
  const centreY = clamp(target.top + target.height / 2 - cardH / 2, EDGE, Math.max(EDGE, vh - cardH - EDGE));
  switch (side) {
    case 'top':
      return { top: Math.max(EDGE, target.top - PAD - GAP - cardH), left: centreX };
    case 'right':
      return { top: centreY, left: target.left + target.width + PAD + GAP };
    case 'left':
      return { top: centreY, left: Math.max(EDGE, target.left - PAD - GAP - cardW) };
    case 'bottom':
    default:
      return { top: clamp(target.top + target.height + PAD + GAP, EDGE, Math.max(EDGE, vh - cardH - EDGE)), left: centreX };
  }
}

export function TourRunner({ run, onFinish, onAbandon }: TourRunnerProps) {
  const steps = useMemo(() => sortedSteps(run.tour), [run.tour]);
  const total = steps.length;
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;

  const mantineReduced = useReducedMotion();
  const prefReduced = usePreferencesStore((s) => s.reducedMotion);
  const reduced = !!(mantineReduced || prefReduced);
  const isPhone = !!useMediaQuery('(max-width: 575px)');

  const [index, setIndex] = useState(0);
  const [showing, setShowing] = useState(false);
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [box, setBox] = useState<Box | null>(null);
  const [cardSize, setCardSize] = useState({ w: 360, h: 220 });
  const [viewport, setViewport] = useState({ w: typeof window !== 'undefined' ? window.innerWidth : 1024, h: typeof window !== 'undefined' ? window.innerHeight : 768 });

  const cardRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null);

  const step: HelpTourStepView | undefined = steps[index];
  const placement = normalisePlacement(step?.placement);

  // ---------------------------------------------------------------- step activation

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;
    const current = steps[index];
    if (!current) return undefined;
    setShowing(false);

    (async () => {
      if (current.route) {
        const loc = locationRef.current;
        const { to, changed } = resolveStepRoute(current.route, loc.pathname, loc.search);
        if (changed) navigate(to, { replace: current.route.trim().startsWith('?') });
      }
      const targetId = (current.target ?? '').trim();
      const wantsTarget = !!targetId && normalisePlacement(current.placement) !== 'center';
      const waitMs = index === 0 ? run.firstWaitMs : LATER_STEP_WAIT_MS;

      let el: HTMLElement | null = null;
      if (wantsTarget) {
        el = await waitFor(() => findAnchor(targetId), waitMs, isCancelled);
      } else if (index === 0 && run.auto) {
        await waitFor(() => pageHasAnchors(run.tour.pageKey), waitMs, isCancelled);
      }
      if (cancelled) return;
      if (index === 0 && run.startDelayMs > 0) {
        await sleep(run.startDelayMs);
        if (cancelled) return;
        // The person may have opened something else meanwhile.
        if (el && !el.isConnected) el = findAnchor(targetId);
      }
      if (el) {
        const small = window.matchMedia?.('(max-width: 575px)').matches;
        const prevMargin = el.style.scrollMarginTop;
        if (small) el.style.scrollMarginTop = '80px';
        el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: small ? 'start' : 'center', inline: 'nearest' });
        if (small) el.style.scrollMarginTop = prevMargin;
        if (!reduced) await sleep(260);
        if (cancelled) return;
      }
      setTarget(el);
      setBox(el ? toBox(el.getBoundingClientRect()) : null);
      setShowing(true);
    })();

    return () => {
      cancelled = true;
    };
    // Only the step index starts a step; the run is fixed for this component (keyed by runId).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // An auto-started tour waits quietly; if the person leaves the page first, drop it.
  useEffect(() => {
    if (run.auto && index === 0 && !showing && location.pathname !== run.startPath) onAbandon();
  }, [location.pathname, run.auto, run.startPath, index, showing, onAbandon]);

  // ---------------------------------------------------------------- positioning

  const measure = useCallback(() => {
    setViewport({ w: window.innerWidth, h: window.innerHeight });
    if (target) {
      if (!target.isConnected) {
        // The element was replaced (re-render): look it up again.
        const id = (steps[index]?.target ?? '').trim();
        const again = id ? findAnchor(id) : null;
        if (again && again !== target) {
          setTarget(again);
          setBox(toBox(again.getBoundingClientRect()));
          return;
        }
        setBox(null);
        return;
      }
      setBox(toBox(target.getBoundingClientRect()));
    }
  }, [target, steps, index]);

  useEffect(() => {
    if (!showing) return undefined;
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    const ro = typeof ResizeObserver !== 'undefined' && target ? new ResizeObserver(schedule) : null;
    if (ro && target) ro.observe(target);
    // Layout can shift without a scroll or resize event (data loading in): check now and then.
    const timer = window.setInterval(schedule, 500);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      ro?.disconnect();
      window.clearInterval(timer);
    };
  }, [showing, target, measure]);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setCardSize((cur) => (Math.abs(cur.w - r.width) > 1 || Math.abs(cur.h - r.height) > 1 ? { w: r.width, h: r.height } : cur));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [showing]);

  // ---------------------------------------------------------------- focus + keys

  const isLast = index >= total - 1;
  const next = useCallback(() => {
    if (isLast) onFinish('completed');
    else setIndex((i) => Math.min(total - 1, i + 1));
  }, [isLast, onFinish, total]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const skip = useCallback(() => onFinish('dismissed'), [onFinish]);

  useEffect(() => {
    if (showing) cardRef.current?.focus({ preventScroll: true });
  }, [showing, index]);

  useEffect(() => {
    const returnTo = returnFocusRef.current;
    return () => {
      if (returnTo && returnTo.isConnected && typeof returnTo.focus === 'function') returnTo.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    if (!showing) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const card = cardRef.current;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        skip();
        return;
      }
      const inCard = !!card && (card === document.activeElement || card.contains(document.activeElement));
      const typing = (document.activeElement as HTMLElement | null)?.tagName === 'TEXTAREA';
      if (inCard && !typing && e.key === 'ArrowRight') {
        e.preventDefault();
        next();
        return;
      }
      if (inCard && !typing && e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        back();
        return;
      }
      if (e.key === 'Tab' && card) {
        const focusables = Array.from(card.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'));
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (!card.contains(active)) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && (active === first || active === card)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [showing, skip, next, back, index]);

  if (typeof document === 'undefined' || !step) return null;

  // While a later step waits for its target, keep the page dimmed so nothing flickers.
  if (!showing) {
    return index > 0 ? createPortal(<div className={`${classes.backdrop} ${classes.dim}`} aria-hidden />, document.body) : null;
  }

  const spotlight = box && placement !== 'center' ? { top: box.top - PAD, left: box.left - PAD, width: box.width + PAD * 2, height: box.height + PAD * 2 } : null;
  const asSheet = isPhone;
  let cardStyle: CSSProperties;
  if (asSheet) {
    cardStyle = {};
  } else if (!spotlight || !box) {
    cardStyle = { top: Math.max(EDGE, (viewport.h - cardSize.h) / 2), left: Math.max(EDGE, (viewport.w - cardSize.w) / 2) };
  } else {
    cardStyle = placeCard(box, placement, cardSize.w, cardSize.h, viewport.w, viewport.h);
  }

  const titleId = `help-tour-title-${run.runId}`;
  const bodyId = `help-tour-body-${run.runId}`;
  const still = reduced ? classes.still : '';

  return createPortal(
    <>
      <div className={`${classes.backdrop} ${spotlight ? '' : classes.dim}`} aria-hidden onClick={(e) => e.stopPropagation()} />
      {spotlight && <div className={`${classes.spotlight} ${still}`} style={spotlight} aria-hidden />}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        className={[classes.card, asSheet ? classes.sheet : '', still].filter(Boolean).join(' ')}
        style={cardStyle}
      >
        <Group justify="space-between" wrap="nowrap" gap="xs" mb={6}>
          <Group gap={6} wrap="nowrap">
            <Text size="xs" c="dimmed" fw={600} aria-live="polite">
              Step {index + 1} of {total}
            </Text>
            {run.preview && (
              <Badge size="xs" variant="light" color="grape" styles={{ root: { textTransform: 'none' } }}>
                Preview
              </Badge>
            )}
          </Group>
          <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Skip the tour" onClick={skip}>
            <IconX size={14} />
          </ActionIcon>
        </Group>
        <Title order={4} id={titleId} mb={6}>
          {step.title || run.tour.title}
        </Title>
        <div id={bodyId}>
          <HelpHtml html={step.bodyHtml} onNavigate={() => onFinish('dismissed', { navigated: true })} />
        </div>
        <Group justify="space-between" wrap="nowrap" gap="xs" mt="md">
          <Button variant="subtle" color="gray" size="compact-sm" onClick={skip}>
            Skip
          </Button>
          <Group gap="xs" wrap="nowrap">
            {index > 0 && (
              <Button variant="default" size="compact-sm" onClick={back}>
                Back
              </Button>
            )}
            <Button size="compact-sm" onClick={next}>
              {isLast ? 'Done' : 'Next'}
            </Button>
          </Group>
        </Group>
      </div>
    </>,
    document.body
  );
}
